import React, { ReactElement, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosResponse } from 'axios';
import styled from 'styled-components';

import { AuthenticationApiResponse } from 'features/auth';
import { Page, Box, Typography, Flex, HelperText, Loading, Button, Copyright, Icons } from 'components';
import { request, useFormikHandles } from 'app/utils';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthInput2 } from 'features/auth/components';

const Form = styled.form`
  flex-grow: 1;
`;

const Icon = styled((props) => <Icons {...props} />)`
  color: ${({ theme }) => theme.colors.primary_400};
  width: 65px;
`;

const ForgotPasswordView = (): ReactElement => {
  const { apiGatewayUrl, tenantUuid, schoolUuid } = useSchool();
  const { setAlert, clearAlert } = useAlert();
  const [showSuccess, setShowSuccess] = useState(false);

  const initialValues: Record<string, any> = {
    email: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      email: Yup.string().email('O e-mail está inválido').required('O e-mail é obrigatório'),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const { email } = values;

      request(
        {
          url: '/api/v1/User/PasswordRecovery',
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            login: email,
            tenantUuid,
            schoolUuid,
          },
        },
        true
      )
        .then((response: AxiosResponse<AuthenticationApiResponse>) => {
          setSubmitting(false);
          setShowSuccess(true);
          resetForm();
        })
        .catch((error) => {
          setSubmitting(false);
          setAlert({
            type: 'error',
            message: 'O e-mail informado não foi encontrado.',
          });
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  useEffect(() => () => clearAlert(), []); // Clear Alert

  return (
    <Page title="Esqueci a senha">
      {formik.isSubmitting && <Loading alpha />}

      <Flex flexGrow={0} flexDirection="column" p={45} bg="dark_350">
        <Typography color="white" fontWeight="bold" upper>
          Esqueceu a senha?
        </Typography>

        <Typography color="gray_500" mt={2}>
          Preencha abaixo seu e-mail para receber as instruções necessárias para criar uma nova senha.
        </Typography>
      </Flex>

      <Box flexGrow={0}>
        <Alert px={45} py={20} />
      </Box>

      <Flex flexDirection="column">
        {!showSuccess ? (
          <Form onSubmit={formik.handleSubmit}>
            <Flex flexGrow={1} flexDirection="column" p={45}>
              {/* email */}
              <Flex flexDirection="column">
                <Typography type="type2" color="white" upper fontWeight="bold">
                  E-mail:
                </Typography>

                <Box position="relative" mt={2}>
                  <AuthInput2
                    name="email"
                    type="text"
                    onChange={formikHandleChange}
                    onFocus={formikHandleFocus}
                    onBlur={formikHandleBlur}
                    value={formik.values.email}
                    data-haserror={formik.touched.email && formik.errors.email}
                    tabIndex={1}
                  />
                </Box>

                {formik.touched.email && formik.errors.email ? <HelperText type="error">{formik.errors.email}</HelperText> : null}
              </Flex>
              {/* /email */}

              <Flex mt={8}>
                <Button width="100%" type="submit" size="medium">
                  Enviar
                </Button>
              </Flex>
            </Flex>
          </Form>
        ) : (
          <Flex flexDirection="column" alignItems="center" justifyContent="flex-start" pt={8}>
            <Icon icon="emailFast" />
            <Typography color="primary_400" fontWeight="bold" upper mt={2}>
              E-mail enviado com sucesso.
            </Typography>
            <Typography color="gray_500" mt={3}>
              Não esqueça de verificar o SPAM.
            </Typography>
          </Flex>
        )}

        <Flex flexGrow={0} justifyContent="center" p={4}>
          <Copyright />
        </Flex>
      </Flex>
    </Page>
  );
};

export default ForgotPasswordView;
